import { createContext, useState, useContext, useMemo } from 'react';
import Drawer from '@mui/material/Drawer';
import { useTranslation } from 'react-i18next';
import { theme } from '@utils/libs/theme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { ThemeButtonIcon } from '@shared/ui/buttons';

const DrawerContext = createContext(null);
const DrawerStateContext = createContext(null);

const INITIAL_STATE = {
  isOpen: false,
  contentComponent: null,
  anchor: 'right',
  title: '',
  className: '',
};

export function DrawerProvider({ children }) {
  const [drawer, setDrawer] = useState(INITIAL_STATE);

  const contextValue = useMemo(
    () => ({
      openDrawer: (contentComponent, props = {}) =>
        setDrawer({
          ...INITIAL_STATE,
          ...props,
          isOpen: true,
          contentComponent,
        }),
      closeDrawer: () => setDrawer((prev) => ({ ...prev, isOpen: false })),
    }),
    [],
  );

  return (
    <DrawerContext.Provider value={contextValue}>
      <DrawerStateContext.Provider value={drawer}>
        {children}
      </DrawerStateContext.Provider>
    </DrawerContext.Provider>
  );
}

export function DrawerViewport() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const drawer = useContext(DrawerStateContext);
  const { closeDrawer } = useDrawerContext();

  const Content = drawer.contentComponent;

  return (
    <Drawer
      className={`drawerFilter ${drawer.className}`}
      anchor={isMobile ? 'bottom' : drawer.anchor}
      open={drawer.isOpen}
      onClose={closeDrawer}
    >
      <div className="customDialog__heading">
        <p className="title-h2m">{t(drawer.title)}</p>
        <ThemeButtonIcon icon="close" onClick={closeDrawer} />
      </div>

      {Content && <Content {...drawer} close={closeDrawer} />}
    </Drawer>
  );
}

export function useDrawerContext() {
  const ctx = useContext(DrawerContext);
  if (!ctx) {
    throw new Error('useDrawerContext must be used within DrawerProvider');
  }
  return ctx;
}
